import React, { useState, useEffect } from 'react';
import {
  Modal, Form, Input, Select, InputNumber, Row, Col, Button, 
  Spin, message, Divider, Typography, Alert, Space, Card, Tag,
} from 'antd';
import { DollarOutlined, FileTextOutlined, EyeOutlined, CheckCircleOutlined, InfoCircleOutlined } from '@ant-design/icons';
import { previewVoucher, confirmVoucher, fetchCashThreshold } from '../api';
import { roundAmount } from '../../../utils/goldCalculation';
import PaymentSplitInput, { DEFAULT_PAYMENT_MODES } from '../../../components/PaymentSplitInput';
import dayjs from 'dayjs';

const { Title, Text } = Typography;
const { TextArea } = Input;

const ADVANCE_CATEGORIES = [
  { value: 'LOAN_PAYMENT', label: 'Loan Payment (to Lender)' },
  { value: 'CUSTOMER_TAKE_HOME', label: 'Customer Take-Home Advance' },
];

const CATEGORY_RECIPIENT_MAP = {
  LOAN_PAYMENT: 'LENDER',
  CUSTOMER_TAKE_HOME: 'CUSTOMER',
};


const VoucherPreviewModal = ({
  open,
  onCancel,
  onSuccess,
  caseId,
  caseData,
  remainingLoan = 0,
  remainingTakeHome = 0,
}) => {
  const [form] = Form.useForm();
  const [messageApi, contextHolder] = message.useMessage();
  
  // Step states
  const [step, setStep] = useState(1); // 1: Form, 2: Preview, 3: Confirming
  const [paymentDetails, setPaymentDetails] = useState(null);
  const [pdfPreview, setPdfPreview] = useState(null);
  const [previewing, setPreviewing] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [cashThreshold, setCashThreshold] = useState(20000);

  // Format amount as whole rupee without decimals
  const formatWholeAmount = (price) =>
    new Intl.NumberFormat('en-IN', { 
      style: 'currency', 
      currency: 'INR',
      minimumFractionDigits: 0, 
      maximumFractionDigits: 0 
    }).format(price || 0);

  
  // Reset state when modal opens/closes
  useEffect(() => {
    if (open) {
      setStep(1);
      setPdfPreview(null);
      setPaymentDetails(null);
      form.resetFields();
      form.setFieldsValue({ paymentSplits: [{ mode: 'BANK_TRANSFER', amount: null, reference: '' }] });
      fetchCashThreshold()
        .then(res => setCashThreshold(res?.data?.cashApprovalThreshold ?? 20000))
        .catch(() => {});
    }
  }, [open, form]);

  // Handle preview generation
  const handlePreview = async (values) => {
    setPreviewing(true);
    try {
      const { paymentSplits, fundId: _ignored, ...rest } = values;
      const totalAmount = roundAmount((paymentSplits || []).reduce((s, p) => s + (Number(p?.amount) || 0), 0));
      const details = {
        ...rest,
        amount: totalAmount,
        paymentSplits,
        paymentRecipient: CATEGORY_RECIPIENT_MAP[values.advanceCategory],
      };

      const result = await previewVoucher(caseId, details);
      
      setPaymentDetails(details);
      setPdfPreview(result.data);
      setStep(2);
      
      messageApi.success('Voucher preview generated successfully');
    } catch (error) {
      console.error('Preview error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to generate preview');
    } finally {
      setPreviewing(false);
    }
  };

  // Handle approval and confirmation
  const handleApprove = async () => {
    setConfirming(true);
    try {
      const result = await confirmVoucher(caseId, paymentDetails);

      if (result?.pendingTreasuryApproval) {
        messageApi.info(
          result?.message
          || 'Advance payment submitted for approval. Check Workflow → My Approvals.'
        );
      } else {
        messageApi.success('Payment recorded and voucher created successfully');
      }
      onSuccess?.(result.data);

      // Close modal after brief delay
      setTimeout(() => {
        setStep(1);
        onCancel?.();
      }, 800);
      
    } catch (error) {
      console.error('Confirm error:', error);
      messageApi.error(error?.response?.data?.message || 'Failed to create voucher');
    } finally {
      setConfirming(false);
    }
  };

  // Handle back to form
  const handleBackToForm = () => {
    setStep(1);
    setPdfPreview(null);
    setPaymentDetails(null);
  };

  // Handle modal close
  const handleCancel = () => {
    setStep(1);
    onCancel?.();
  };

  const renderFormStep = () => (
    <Form
      form={form}
      layout="vertical"
      onFinish={handlePreview}
    >
      <Row gutter={16}>
        <Col span={24}>
          <Form.Item
            name="advanceCategory"
            label="Advance Category"
            rules={[{ required: true, message: 'Select advance category' }]}
          >
            <Select
              placeholder="Select category"
              onChange={(val) => {
                form.setFieldsValue({
                  paymentRecipient: CATEGORY_RECIPIENT_MAP[val],
                });
                // Only pre-fill the splits if they haven't been touched yet
                // (i.e. still a single row with no amount entered).
                // This prevents wiping user-entered CASH/BANK selections.
                const currentSplits = form.getFieldValue('paymentSplits') || [];
                const untouched = currentSplits.length <= 1
                  && !currentSplits[0]?.amount;
                if (untouched) {
                  const maxVal = val === 'LOAN_PAYMENT' ? remainingLoan : Math.max(0, remainingTakeHome);
                  form.setFieldsValue({
                    paymentSplits: [{ mode: 'BANK_TRANSFER', amount: maxVal > 0 ? maxVal : null, reference: '' }],
                  });
                }
              }}
            >
              {ADVANCE_CATEGORIES.map((cat) => (
                <Select.Option key={cat.value} value={cat.value}>{cat.label}</Select.Option>
              ))}
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Row gutter={16}>
        <Col span={24}>
          <Form.Item name="paymentRecipient" label="Recipient">
            <Select disabled>
              <Select.Option value="CUSTOMER">Customer</Select.Option>
              <Select.Option value="LENDER">Lender</Select.Option>
            </Select>
          </Form.Item>
        </Col>
      </Row>

      <Divider plain style={{ margin: '8px 0 12px' }}>Payment Breakdown</Divider>

      <Form.Item noStyle shouldUpdate={(prev, cur) => prev.advanceCategory !== cur.advanceCategory}>
        {({ getFieldValue }) => {
          const cat = getFieldValue('advanceCategory');
          const maxVal = cat === 'LOAN_PAYMENT'
            ? remainingLoan
            : cat === 'CUSTOMER_TAKE_HOME'
              ? Math.max(0, remainingTakeHome)
              : 0;
          const cashBlocked = maxVal > cashThreshold;

          return (
            <>
              {cashBlocked && (
                <Alert
                  type="info"
                  showIcon
                  icon={<InfoCircleOutlined />}
                  message={`Cash payments are limited to ₹${cashThreshold.toLocaleString('en-IN')} (Cash Auto-Approval Limit). Use Bank Transfer, UPI, or Cheque.`}
                  style={{ marginBottom: 10 }}
                />
              )}
              {/* Bank rows carry no reference here: the UTR does not exist until the
                  transfer is actually made, which happens when the approver approves.
                  They enter it against each split on the Approve action. */}
              <PaymentSplitInput
                fieldName="paymentSplits"
                maxAmount={maxVal}
                paymentModes={cashBlocked
                  ? DEFAULT_PAYMENT_MODES.filter(m => m.value !== 'CASH')
                  : DEFAULT_PAYMENT_MODES}
                referenceModes={['CASH']}
                referenceHint="UTR entered by approver"
              />
            </>
          );
        }}
      </Form.Item>

      <Form.Item
        noStyle
        shouldUpdate={(prev, cur) => prev.paymentSplits !== cur.paymentSplits}
      >
        {({ getFieldValue }) => {
          const splits = getFieldValue('paymentSplits') || [];
          const hasBank = splits.some(s => ['BANK_TRANSFER', 'UPI', 'CHEQUE'].includes(s?.mode));
          // A bank payout's reference is the approver's to record, per split, on
          // the Approve action — there is nothing meaningful to type here.
          if (hasBank) return null;
          return (
            <Form.Item name="transactionReference" label="Transaction Reference">
              <Input placeholder="e.g. receipt no." />
            </Form.Item>
          );
        }}
      </Form.Item>

      <Form.Item name="remarks" label="Remarks">
        <TextArea rows={2} placeholder="Optional notes" />
      </Form.Item>

      <Row justify="end">
        <Space>
          <Button onClick={handleCancel}>Cancel</Button>
          <Button
            type="primary"
            icon={<EyeOutlined />}
            htmlType="submit"
            loading={previewing}
          >
            Generate Preview
          </Button>
        </Space>
      </Row>
    </Form>
  );

  const renderPreviewStep = () => (
    <div>
      {/* Payment Summary */}
      <Card size="small" style={{ marginBottom: 16 }}>
        <Row gutter={16}>
          <Col span={6}>
            <Text type="secondary">Category</Text>
            <br />
            <Tag color={paymentDetails?.advanceCategory === 'LOAN_PAYMENT' ? 'blue' : 'green'}>
              {paymentDetails?.advanceCategory === 'LOAN_PAYMENT' ? 'Loan Payment' : 'Customer Take-Home'}
            </Tag>
          </Col>
          <Col span={6}>
            <Text type="secondary">Total Amount</Text>
            <br />
            <Text strong style={{ fontSize: 16 }}>{formatWholeAmount(paymentDetails?.amount)}</Text>
          </Col>
          <Col span={6}>
            <Text type="secondary">Payment Mode</Text>
            <br />
            {paymentDetails?.paymentSplits?.length > 1 ? (
              <Space direction="vertical" size={2}>
                {paymentDetails.paymentSplits.map((s, i) => (
                  <Tag key={i} color="blue">{s.mode?.replace(/_/g, ' ')} — {formatWholeAmount(s.amount)}</Tag>
                ))}
              </Space>
            ) : (
              <Text>{paymentDetails?.paymentSplits?.[0]?.mode?.replace(/_/g, ' ') || '—'}</Text>
            )}
          </Col>
          <Col span={6}>
            <Text type="secondary">Settlement</Text>
            <br />
            <Tag color="purple">
              {paymentDetails?.paymentSplits?.some(s => ['BANK_TRANSFER','UPI','CHEQUE'].includes(s.mode))
                ? 'Central Fund'
                : 'Branch Cash'}
            </Tag>
          </Col>
        </Row>
      </Card>

      {/* PDF Preview */}
      <Divider orientation="left" plain>Voucher Preview</Divider>
      
      {pdfPreview?.pdfBase64 ? (
        <div style={{ border: '1px solid #d9d9d9', borderRadius: 6, overflow: 'hidden' }}>
          <iframe
            src={`data:application/pdf;base64,${pdfPreview.pdfBase64}`}
            width="100%"
            height="600px"
            title="Voucher Preview"
            style={{ border: 'none' }}
          />
        </div>
      ) : (
        <Alert type="error" message="Failed to load PDF preview" showIcon />
      )}

      <Divider />

      <Row justify="space-between">
        <Button onClick={handleBackToForm} disabled={confirming}>
          Back to Form
        </Button>
        <Space>
          <Button onClick={handleCancel} disabled={confirming}>
            Cancel
          </Button>
          <Button
            type="primary"
            icon={<CheckCircleOutlined />}
            loading={confirming}
            onClick={handleApprove}
            size="large"
          >
            Submit for Approval
          </Button>
        </Space>
      </Row>
    </div>
  );

  return (
    <>
      {contextHolder}
      <Modal
        title={
          <Space>
            <FileTextOutlined />
            {step === 1 ? 'Create Advance Payment Voucher' : 'Voucher Preview & Approval'}
          </Space>
        }
        open={open}
        onCancel={handleCancel}
        footer={null}
        width={step === 1 ? 600 : 900}
        destroyOnClose
        maskClosable={step === 1}
      >
        {/* Case Info Header */}
        <Alert
          type="info"
          showIcon
          className="mb-3"
          message={
            <Space split={<Divider type="vertical" />} size="small">
              <span><strong>Case:</strong> {caseData?.caseId}</span>
              <span><strong>Customer:</strong> {caseData?.customerId?.name}</span>
              <span><strong>Lender:</strong> {caseData?.lenderName}</span>
            </Space>
          }
        />

        {step === 1 && renderFormStep()}
        {step === 2 && renderPreviewStep()}
      </Modal>
    </>
  );
};

export default VoucherPreviewModal;